/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import useRootStore from '@store/useRootStore'
import { Button } from '@styles/button.style'
import { UsageGuideVideoWrap } from './index.style'
import { isMobile } from 'react-device-detect'
import classNames from 'classnames'

export type UsageGuideVideoPopupEntry = 'join' | 'login' | 'manual'

interface UsageGuideVideoPlaybackPolicy {
  forced: boolean
  initialMuted: boolean
}

const USAGE_GUIDE_VIDEO_PLAYBACK_POLICY: Record<UsageGuideVideoPopupEntry, UsageGuideVideoPlaybackPolicy> = {
  // 가입 완료 확인 클릭에서 열리므로 소리 있는 강제 시청을 시도한다.
  join: { forced: true, initialMuted: false },
  // 저장 세션 재실행에서는 WebView가 소리 있는 자동재생을 거부할 수 있어 음소거로 시작한다.
  login: { forced: true, initialMuted: true },
  // 사용자가 메뉴에서 직접 연 재시청은 자유롭게 조작한다.
  manual: { forced: false, initialMuted: false },
}

export const getUsageGuideVideoPlaybackPolicy = (entry: UsageGuideVideoPopupEntry): UsageGuideVideoPlaybackPolicy => {
  return USAGE_GUIDE_VIDEO_PLAYBACK_POLICY[entry]
}

interface UsageGuideVideoPopupProps {
  entry: UsageGuideVideoPopupEntry
  setRemainingSeconds: (value: number | null) => void
  onRequestClose: () => void
  onPlaybackCompletionChange: (completed: boolean) => void
}

const formatSeconds = (seconds: number) => {
  return Number.isFinite(seconds) ? Math.max(0, Math.round(seconds)) : 0
}

const UsageGuideVideoPopup = ({
  entry, setRemainingSeconds, onRequestClose, onPlaybackCompletionChange,
}: UsageGuideVideoPopupProps) => {
  const { globalStore } = useRootStore()
  const { forced, initialMuted } = getUsageGuideVideoPlaybackPolicy(entry)
  const { videoFileName } = globalStore.getUsageGuideVideoPopupOptions()
  const videoUrl = globalStore.getUsageGuideVideoUrl(videoFileName)

  const videoRef = useRef<HTMLVideoElement>(null)
  const [isError, setIsError] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const isLocked = globalStore.usageGuideVideoPopupPlaybackLocked

  // 완료 기록은 팝업 공통 닫힘(X·확인)을 같은 경로로 처리하도록 부모에 위임한다.
  const handleClose = () => {
    globalStore.setUsageGuideVideoPopupPlaybackLocked(false)
    onRequestClose()
  }

  useEffect(() => {
    globalStore.setUsageGuideVideoPopupPlaybackLocked(forced)
    setIsError(false)
    onPlaybackCompletionChange(false)
    setCurrentTime(0)

    const video = videoRef.current
    if (!videoUrl) {
      console.error('[UsageGuideVideoPopup] video URL is empty')
      setIsError(true)
      onPlaybackCompletionChange(false)
      globalStore.setUsageGuideVideoPopupPlaybackLocked(false)
      return () => globalStore.setUsageGuideVideoPopupPlaybackLocked(false)
    }
    if (video) {
      video.currentTime = 0
      // 브라우저 자동재생 정책으로 play()가 거부될 수 있다. 이 경우 강제 모드의
      // 강제 시청을 유지할 수 없으므로 오류 상태로 전환해 탈출을 허용한다.
      video.play().catch(() => {
        if (!forced) {
          return
        }
        console.error('[UsageGuideVideoPopup] automatic playback was rejected', { videoUrl })
        setIsError(true)
        onPlaybackCompletionChange(false)
        globalStore.setUsageGuideVideoPopupPlaybackLocked(false)
      })
    }
    return () => globalStore.setUsageGuideVideoPopupPlaybackLocked(false)
  }, [videoUrl, forced])

  const handleLoadedMetadata = () => {
    setDuration(videoRef.current?.duration ?? 0)
  }

  const handleTimeUpdate = () => {
    setCurrentTime(videoRef.current?.currentTime ?? 0)
  }

  const handleEnded = () => {
    onPlaybackCompletionChange(true)
    globalStore.setUsageGuideVideoPopupPlaybackLocked(false)
  }

  // 영상 로드/재생 실패 시 영상 대신 안내 이미지를 보여주고, 강제 모드라도 사용자를
  // 계속 가두지 않도록 즉시 잠금을 해제한다(ended가 영원히 오지 않아 못 나가는 상황 방지).
  const handleVideoError = () => {
    console.error('[UsageGuideVideoPopup] video playback failed', { videoUrl })
    setIsError(true)
    onPlaybackCompletionChange(false)
    globalStore.setUsageGuideVideoPopupPlaybackLocked(false)
  }

  const handleRetryPlay = () => {
    videoRef.current?.play().catch(() => undefined)
  }

  const progressPercent = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0

  /* 헤더 우측 상단 남은 시간 */
  useEffect(() => {
    const value = formatSeconds(duration > 0 ? duration - currentTime : 0)
    setRemainingSeconds(isLocked ? value : null)
  }, [duration, currentTime, isLocked])

  const videoWrapClass = classNames('usage-guide-video-popup__video-wrap',
    { pc: !isMobile },
  )

  const progressBarClass = classNames('usage-guide-video-popup__progress-bar',
    { full: progressPercent === 100 },
  )

  return (
      <UsageGuideVideoWrap>
        <section className="usage-guide-video-popup__content">
          <div className={videoWrapClass}>
            {isError ? (
              <div className="usage-guide-video-popup__error-fallback" aria-label="영상을 불러오지 못했습니다"/>
            ) : (
              <React.Fragment>
                <div className="usage-guide-video-popup__progress">
                  <div className="usage-guide-video-popup__progress-track">
                    <div
                      className={progressBarClass}
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>
                {/* 자막 파일은 퍼블리싱 영상 산출물 확정 시 연결한다. */}
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                  ref={videoRef}
                  controls={!isLocked}
                  muted={initialMuted}
                  playsInline
                  preload="auto"
                  onLoadedMetadata={handleLoadedMetadata}
                  onTimeUpdate={handleTimeUpdate}
                  onEnded={handleEnded}
                  onError={handleVideoError}
                >
                  {videoUrl && <source src={videoUrl} type="video/mp4"/>}
                </video>
                {isLocked && (
                  <div
                    className="usage-guide-video-popup__interaction-blocker"
                    onClick={handleRetryPlay}
                    role="presentation"
                  />
                )}
              </React.Fragment>
            )}
          </div>
          {!isLocked && (
            <Button primary className="usage-guide-video-popup__confirm" onClick={handleClose}>
              확인
            </Button>
          )}
        </section>
      </UsageGuideVideoWrap>
  )
}

export default observer(UsageGuideVideoPopup)
